import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { MdArrowBack, MdAttachFile, MdCancel, MdCheckCircle, MdDeleteForever, MdEdit, MdEmail, MdMap, MdPhone, MdSwapHoriz } from 'react-icons/md';
import { getData, putData, patchData, deleteData } from '../../Webservice/Webservice';
import ChangeOwnerDialog from '../../components/Products/ChangeOwnerDialog';
import { DOC_TYPES, FONT, SellerAvatar, StatusChip, authToken, fmtDate, fmtDateTime } from './sellerShared';

const Row = ({ k, v, mono }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' } }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{k}</Typography>
    <Typography component="div" sx={{ fontFamily: mono ? 'monospace' : FONT, fontSize: 13, fontWeight: 700, color: '#0f172a', textAlign: 'right', wordBreak: 'break-word' }}>{v === undefined || v === null || v === '' ? '—' : v}</Typography>
  </Box>
);

const Card = ({ title, children, sx }) => (
  <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0', ...sx }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, mb: 0.75 }}>{title}</Typography>
    {children}
  </Paper>
);

const maskAccount = (n) => (n ? `•••• ${String(n).slice(-4)}` : '');

function SellerDetails({ sellerId, onEdit }) {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'approve' | 'reject' | 'delete' }

  const [prods, setProds] = useState(null);
  const [picked, setPicked] = useState({});
  const [moving, setMoving] = useState(false);
  const loadProds = useCallback(() => {
    setProds(null);
    setPicked({});
    getData('product/list', { sellerId, limit: 500 }, authToken(), false)
      .then((r) => setProds(Array.isArray(r?.products) ? r.products : []))
      .catch(() => setProds([]));
  }, [sellerId]);
  useEffect(() => { if (tab === 5 && prods === null) loadProds(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickedList = (prods || []).filter((p) => picked[p._id]).map((p) => ({ _id: p._id, title: p.title }));

  const load = useCallback(async () => {
    try {
      const res = await getData(`seller/${sellerId}`, {}, authToken(), false);
      if (!res?._id) throw new Error('missing');
      setS(res);
      setError('');
    } catch (e) {
      setError('Could not load this seller.');
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => { load(); }, [load]);

  const act = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'delete') {
        await deleteData(`seller/delete/${sellerId}`, authToken());
        navigate('/sellermanagement');
        return;
      }
      const status = confirm.kind === 'approve' ? 'approved' : 'rejected';
      await putData(`seller/statusUpdate/${sellerId}`, { status }, authToken());
      setS((x) => ({ ...x, status }));
    } catch (e) {
      setError('That action failed. Please try again.');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      await patchData(`seller/toggle-active/${sellerId}`, {}, authToken());
      setS((x) => ({ ...x, isActive: !x.isActive }));
    } catch (e) {
      setError('Could not change the active state.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  if (error && !s) return <Box sx={{ p: 3 }}><Button startIcon={<MdArrowBack />} onClick={() => navigate('/sellermanagement')} sx={{ textTransform: 'none', fontWeight: 700 }}>Back</Button><Alert severity="error" sx={{ mt: 2 }}>{error}</Alert></Box>;

  const bank = s.bankDetails || {};
  const docs = DOC_TYPES.map((d) => ({ ...d, url: s[d.id] })).concat(s.eSignature ? [{ id: 'eSignature', label: 'E-signature', url: s.eSignature }] : []);
  const docCount = docs.filter((d) => d.url).length;
  const creator = s.createdBy && typeof s.createdBy === 'object' ? `${s.createdBy.firstName || ''} ${s.createdBy.lastName || ''}`.trim() : '';
  const updater = s.updatedBy && typeof s.updatedBy === 'object' ? `${s.updatedBy.firstName || ''} ${s.updatedBy.lastName || ''}`.trim() : '';
  const hasMap = s.storeLatitude && s.storeLongitude;
  const canApprove = s.status !== 'approved';
  const canReject = s.status !== 'rejected';

  const kpis = [
    { label: 'Products listed', value: s.stats?.products ?? 0, bg: '#eef2ff', fg: '#4338ca' },
    { label: 'Product requests', value: s.stats?.requests ?? 0, bg: '#e0f2fe', fg: '#0369a1' },
    { label: 'Commission', value: s.commissionPercentage != null ? `${s.commissionPercentage}%` : '—', bg: '#fef3c7', fg: '#b45309' },
    { label: 'Documents', value: `${docCount}/${DOC_TYPES.length}`, bg: docCount ? '#d1fae5' : '#fee2e2', fg: docCount ? '#047857' : '#b91c1c' },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <IconButton onClick={() => navigate('/sellermanagement')} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}><MdArrowBack size={20} /></IconButton>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>Sellers / <b style={{ color: '#0f172a' }}>{s.business_name || s.name}</b></Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: '16px', color: '#fff', background: 'linear-gradient(135deg,#0f172a,#334155)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2.5 }}>
          <SellerAvatar seller={s} size={72} />
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, lineHeight: 1.2 }}>{s.business_name || s.name}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.8 }}>{s.name}{s.sellerCode ? ` · ${s.sellerCode}` : ''} · joined {fmtDate(s.createdAt)}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <StatusChip status={s.status} />
              <Chip size="small" label={s.isActive ? 'Active' : 'Inactive'} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: s.isActive ? '#dcfce7' : '#f1f5f9', color: s.isActive ? '#166534' : '#475569' }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canApprove && <Button onClick={() => setConfirm({ kind: 'approve' })} startIcon={<MdCheckCircle />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#10b981', color: '#fff', '&:hover': { background: '#059669' } }}>Approve</Button>}
            {canReject && <Button onClick={() => setConfirm({ kind: 'reject' })} startIcon={<MdCancel />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: 'rgba(248,113,113,.25)', color: '#fecaca', '&:hover': { background: 'rgba(248,113,113,.4)' } }}>Reject</Button>}
            <Button onClick={onEdit} startIcon={<MdEdit />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: 'rgb(255 209 2)', color: '#000', '&:hover': { background: 'rgb(255 190 0)' } }}>Edit</Button>
            <Button onClick={() => setConfirm({ kind: 'delete' })} startIcon={<MdDeleteForever />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: 'rgba(255,255,255,.12)', color: '#fff', '&:hover': { background: 'rgba(255,255,255,.22)' } }}>Delete</Button>
          </Box>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1.5, mb: 2.5 }}>
          {kpis.map((k) => (
            <Paper key={k.label} elevation={0} sx={{ p: 2, borderRadius: '14px', background: k.bg }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: k.fg, opacity: 0.85 }}>{k.label}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 22, fontWeight: 800, color: k.fg }}>{k.value}</Typography>
            </Paper>
          ))}
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, '& .MuiTab-root': { fontFamily: FONT, fontWeight: 800, textTransform: 'none', fontSize: 14 }, '& .MuiTabs-indicator': { background: 'rgb(255 190 0)', height: 3 } }}>
          {['Overview', 'Business', `Documents (${docCount})`, 'Store', 'Payout', 'Products'].map((t) => <Tab key={t} label={t} />)}
        </Tabs>

        {tab === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <Card title="Primary contact">
              <Row k="Name" v={s.name} />
              <Row k="Email" v={s.email && <><MdEmail style={{ verticalAlign: 'middle', marginRight: 4 }} />{s.email}</>} />
              <Row k="Mobile" v={s.mobile && <><MdPhone style={{ verticalAlign: 'middle', marginRight: 4 }} />{s.mobile}</>} />
            </Card>
            <Card title="Secondary contact">
              {s.contactPerson2Name ? (
                <>
                  <Row k="Name" v={s.contactPerson2Name} />
                  <Row k="Email" v={s.contactPerson2Email} />
                  <Row k="Mobile" v={s.contactPerson2Mobile} />
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', mt: 1 }}>Can also sign in to the seller panel.</Typography>
                </>
              ) : (
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#94a3b8', py: 1 }}>No secondary contact added.</Typography>
              )}
            </Card>
            <Card title="Account" sx={{ gridColumn: { md: '1 / -1' } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #f1f5f9' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>Active (can sign in and sell)</Typography>
                <Switch size="small" disabled={busy} checked={!!s.isActive} onChange={toggleActive} />
              </Box>
              <Row k="Approval status" v={<StatusChip status={s.status} />} />
              <Row k="Seller code" v={s.sellerCode} mono />
              <Row k="Registration" v={s.isRegistrationComplete === false ? 'Incomplete (invited, not finished)' : 'Complete'} />
              <Row k="Created by" v={creator || 'Self registration'} />
              <Row k="Last updated" v={`${fmtDateTime(s.updatedAt)}${updater ? ` · ${updater}` : ''}`} />
            </Card>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <Card title="Business">
              <Row k="Business name" v={s.business_name} />
              <Row k="Seller license" v={s.sellerLicenseNumber} mono />
              <Row k="Address" v={s.businessAddress} />
              <Row k="City" v={s.businessCity} />
              <Row k="State" v={s.businessState} />
              <Row k="Pincode" v={s.businessPincode} />
            </Card>
            <Card title="Tax details">
              <Row k="GST number" v={s.gstNumber} mono />
              <Row k="Business PAN" v={s.businessPAN} mono />
            </Card>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
            {docs.map((d) => (
              <Paper key={d.id} elevation={0} sx={{ p: 1.75, borderRadius: '14px', border: `1.5px solid ${d.url ? '#86efac' : '#e2e8f0'}`, background: d.url ? '#f0fdf4' : '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ color: d.url ? '#16a34a' : '#cbd5e1', display: 'flex' }}>{d.url ? <MdCheckCircle size={24} /> : <MdAttachFile size={24} />}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5 }}>{d.label}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{d.url ? 'Uploaded' : 'Not uploaded'}</Typography>
                </Box>
                {d.url && <Button size="small" href={d.url} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none', fontWeight: 800 }}>Open</Button>}
              </Paper>
            ))}
          </Box>
        )}

        {tab === 3 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <Card title="Store address">
              <Row k="Address" v={s.storeAddress} />
              <Row k="Area" v={s.storeArea} />
              <Row k="Landmark" v={s.storeNearbyLandmark} />
              <Row k="City" v={s.storeCity} />
              <Row k="State" v={s.storeState} />
              <Row k="Pincode" v={s.storePincode} />
            </Card>
            <Card title="Location & about">
              <Row k="Coordinates" v={hasMap ? `${s.storeLatitude}, ${s.storeLongitude}` : ''} mono />
              {hasMap && <Button startIcon={<MdMap />} href={`https://www.google.com/maps?q=${s.storeLatitude},${s.storeLongitude}`} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none', fontWeight: 800, mt: 0.5 }}>Open in Google Maps</Button>}
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#334155', lineHeight: 1.6, mt: 1.5 }}>{s.storeDescription || 'No description added.'}</Typography>
            </Card>
          </Box>
        )}

        {tab === 4 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <Card title="Commission">
              <Row k="Platform commission" v={s.commissionPercentage != null ? `${s.commissionPercentage}% of each sale` : ''} />
            </Card>
            <Card title="Bank details">
              <Row k="Account holder" v={bank.accountHolder} />
              <Row k="Bank" v={bank.bankName} />
              <Row k="Account number" v={maskAccount(bank.accountNumber)} mono />
              <Row k="IFSC" v={bank.ifsc} mono />
              <Row k="UPI ID" v={bank.upiId} />
              {!bank.accountNumber && !bank.upiId && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#b45309', mt: 1 }}>No payout details yet. Payouts can't be released until they're added.</Typography>}
            </Card>
          </Box>
        )}
        {tab === 5 && (
          <Card title="Products owned by this seller">
            {prods === null && <CircularProgress size={22} sx={{ m: 2 }} />}
            {prods && prods.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', py: 1 }}>No products are owned by this seller.</Typography>}
            {prods && prods.length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Checkbox size="small" checked={pickedList.length === prods.length} indeterminate={pickedList.length > 0 && pickedList.length < prods.length}
                    onChange={(e) => setPicked(e.target.checked ? Object.fromEntries(prods.map((p) => [p._id, true])) : {})} />
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, flex: 1 }}>{pickedList.length ? `${pickedList.length} selected` : `${prods.length} products. Select the ones to move.`}</Typography>
                  <Button size="small" variant="contained" startIcon={<MdSwapHoriz />} disabled={!pickedList.length} onClick={() => setMoving(true)} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', '&:hover': { background: 'rgb(255 190 0)' } }}>Change owner</Button>
                </Box>
                {prods.map((p) => (
                  <Box key={p._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderTop: '1px solid #f1f5f9' }}>
                    <Checkbox size="small" checked={!!picked[p._id]} onChange={() => setPicked((m) => ({ ...m, [p._id]: !m[p._id] }))} />
                    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, flex: 1 }}>{p.title}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{p.status}</Typography>
                  </Box>
                ))}
              </>
            )}
          </Card>
        )}
      </Box>

      <ChangeOwnerDialog open={moving} products={pickedList} onClose={() => setMoving(false)} onDone={loadProds} />

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>{confirm?.kind === 'approve' ? 'Approve seller?' : confirm?.kind === 'reject' ? 'Reject seller?' : 'Delete seller?'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT }}>
            {confirm?.kind === 'approve' && <><b>{s.business_name || s.name}</b> will be able to list and sell products. They are emailed about the decision.</>}
            {confirm?.kind === 'reject' && <><b>{s.business_name || s.name}</b> will not be able to sell. They are emailed about the decision.</>}
            {confirm?.kind === 'delete' && <><b>{s.business_name || s.name}</b> will be removed and archived, along with their menu access, assigned products and product requests. This can't be undone from the panel.</>}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirm(null)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={act} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: confirm?.kind === 'approve' ? '#10b981' : '#dc2626', '&:hover': { background: confirm?.kind === 'approve' ? '#059669' : '#b91c1c' } }}>
            {busy ? 'Working…' : confirm?.kind === 'approve' ? 'Yes, approve' : confirm?.kind === 'reject' ? 'Yes, reject' : 'Yes, delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SellerDetails;
